import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import CourseDetails from './components/CourseDetails';
import FormSuccessPopup from './components/FormSuccessPopup';
import BackToTop from './components/BackToTop';
import FormLoader from './components/FormLoader';
import PixelTracker from './components/PixelTracker';
import { courseToSlug, slugToCourse } from './utils/courseMapping';
import './App.css';

function AppContent() {
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showBookDemoModal, setShowBookDemoModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Book a Free Demo');
  const [showCareerGuidanceModal, setShowCareerGuidanceModal] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [bookDemoFormData, setBookDemoFormData] = useState({ 
    fullName: '', 
    mobile: '', 
    email: '', 
    location: '', 
    category: '', 
    course: '',
    customCourse: ''
  });
  const [guidanceFormData, setGuidanceFormData] = useState({ 
    fullName: '', 
    mobile: '', 
    email: '', 
    location: '', 
    category: '', 
    course: '',
    customCourse: ''
  });
  const [enrollmentFormData, setEnrollmentFormData] = useState({ 
    fullName: '', 
    mobile: '', 
    email: '', 
    location: '', 
    category: '', 
    course: '',
    customCourse: ''
  });
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState('success');
  const [popupMessage, setPopupMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Disable browser scroll restoration globally
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Force scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [selectedCourse]);

  const courseCategories = {
    'trending-courses': {
      name: 'Trending Courses',
      courses: ['Full Stack Development', 'Data Science & AI', 'Cloud Computing (AWS/Azure)', 'DevOps Engineering', 'Cybersecurity']
    },
    'programming': {
      name: 'Programming Languages',
      courses: ['Python Programming', 'Java Development', 'JavaScript & TypeScript', 'C++ Programming', 'React & Node.js']
    },
    'data-science': {
      name: 'Data Science & Analytics',
      courses: ['Machine Learning', 'Deep Learning', 'Data Analytics', 'Big Data & Hadoop', 'Tableau & Power BI']
    },
    'cloud-computing': {
      name: 'Cloud & DevOps',
      courses: ['AWS Solutions Architect', 'Azure Administrator', 'Google Cloud Platform', 'Docker & Kubernetes', 'CI/CD Pipelines']
    },
    'web-development': {
      name: 'Web Development',
      courses: ['MERN Stack', 'MEAN Stack', 'Django Development', 'PHP & Laravel', 'Vue.js Development']
    },
    'mobile-development': {
      name: 'Mobile App Development',
      courses: ['React Native', 'Flutter', 'iOS Development', 'Android Development', 'Xamarin']
    },
    'database': {
      name: 'Database Management',
      courses: ['MySQL & PostgreSQL', 'MongoDB', 'Oracle Database', 'SQL Server', 'Firebase']
    },
    'cybersecurity': {
      name: 'Cybersecurity',
      courses: ['Ethical Hacking', 'Network Security', 'Application Security', 'Cloud Security', 'Security Operations']
    },
    'testing': {
      name: 'Software Testing',
      courses: ['Manual Testing', 'Selenium Automation', 'API Testing', 'Performance Testing', 'Mobile Testing']
    },
    'design': {
      name: 'UI/UX Design',
      courses: ['UI/UX Design', 'Figma & Adobe XD', 'Graphic Design', 'Web Design', 'Mobile App Design']
    },
    'digital-marketing': {
      name: 'Digital Marketing',
      courses: ['SEO & SEM', 'Social Media Marketing', 'Content Marketing', 'Email Marketing', 'Google Analytics']
    },
    'business-intelligence': {
      name: 'Business Intelligence',
      courses: ['Power BI', 'Tableau', 'QlikView', 'Data Warehousing', 'Business Analytics']
    },
    'others': {
      name: 'Others',
      courses: ['Others']
    }
  };

  // Modal overflow handling is done by individual modals
  // Removed to prevent conflicts with Header mobile menu overflow control

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBookDemoModal(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleCourseSelect = (course) => {
    window.scrollTo(0, 0);
    setSelectedCourse(course);
    const courseSlug = courseToSlug(course);
    navigate(`/courses/${courseSlug}`);
  };

  const handleHomeClick = () => {
    window.scrollTo(0, 0);
    setSelectedCourse(null);
    navigate('/');
  };

  const handleAboutClick = () => {
    window.scrollTo(0, 0);
    navigate('/about');
  };

  const handleContactClick = () => {
    window.scrollTo(0, 0);
    navigate('/contact');
  };

  const handleEnrollClick = () => {
    setShowEnrollmentModal(true);
  };

  const handleBookDemoClick = () => {
    setModalTitle('Book a Free Demo');
    setShowBookDemoModal(true);
  };

  const handleConnectClick = () => {
    setModalTitle('Connect With Us');
    setShowBookDemoModal(true);
  };

  const handleCareerGuidanceClick = () => {
    setShowCareerGuidanceModal(true);
  };

  const handleBookDemoCategoryChange = (e) => {
    const newCategory = e.target.value;
    setBookDemoFormData({ ...bookDemoFormData, category: newCategory, course: '' });
  };

  const handleGuidanceCategoryChange = (e) => {
    const newCategory = e.target.value;
    setGuidanceFormData({ ...guidanceFormData, category: newCategory, course: '' });
  };

  const handleEnrollmentCategoryChange = (e) => {
    const newCategory = e.target.value;
    setEnrollmentFormData({ ...enrollmentFormData, category: newCategory, course: '' });
  };

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={
          <HomePage 
            onCourseSelect={handleCourseSelect}
            onEnrollClick={handleEnrollClick}
            onBookDemoClick={handleBookDemoClick}
            onCareerGuidanceClick={handleCareerGuidanceClick}
            onHomeClick={handleHomeClick}
            onAboutClick={handleAboutClick}
            onContactClick={handleContactClick}
            activePage="home"
            selectedCourse={selectedCourse}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        } />
        <Route path="/about" element={
          <AboutPage 
            onHomeClick={handleHomeClick}
            onConnectClick={handleConnectClick}
            onAboutClick={handleAboutClick}
            onContactClick={handleContactClick}
            onCourseSelect={handleCourseSelect}
            onEnrollClick={handleEnrollClick}
            activePage="about"
            selectedCourse={selectedCourse}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        } />
        <Route path="/contact" element={
          <ContactPage 
            onHomeClick={handleHomeClick}
            onAboutClick={handleAboutClick}
            onContactClick={handleContactClick}
            onCourseSelect={handleCourseSelect}
            onEnrollClick={handleEnrollClick}
            activePage="contact"
            selectedCourse={selectedCourse}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        } />
        <Route path="/courses/:courseSlug" element={
          <CourseDetailsWrapper 
            onBackClick={handleHomeClick}
            onEnrollClick={handleEnrollClick}
            onCourseSelect={handleCourseSelect}
            onHomeClick={handleHomeClick}
            onAboutClick={handleAboutClick}
            onContactClick={handleContactClick}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        } />
      </Routes>

      {/* Global Book Free Demo Modal - Independent of any section */}
      {showBookDemoModal && ReactDOM.createPortal((
        <div onClick={() => setShowBookDemoModal(false)} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999999999,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <div onClick={(e) => e.stopPropagation()} className="book-demo-modal" style={{
            background: 'linear-gradient(135deg, #002a5c 0%, #004080 30%, #0066cc 70%, #3b82f6 100%)',
            backdropFilter: 'blur(25px)',
            borderRadius: '25px',
            padding: '1rem',
            paddingTop: '2rem',
            width: '95%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.4), inset 0 -2px 4px rgba(0, 0, 0, 0.1), 0 15px 35px rgba(59, 130, 246, 0.25)',
            border: '2px solid rgba(255, 255, 255, 0.3)'
          }}>
            <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowBookDemoModal(false); }} style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              fontSize: '2rem',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'scale(1)';
            }}>×</div>

            <h3 style={{
              color: '#ffffff',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '0.3rem',
              marginTop: '1rem',
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>{modalTitle}</h3>

            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.75rem',
              textAlign: 'center',
              marginBottom: '0.8rem',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
            }}>Fill your details to enroll for the course</p>

            <form onSubmit={async (e) => { 
              e.preventDefault();
              if (isSubmitting) return;
              
              // Validate mobile number
              if (bookDemoFormData.mobile.length !== 10) {
                alert('Please enter a valid 10-digit mobile number');
                return;
              }
              
              setIsSubmitting(true);
              const submissionData = {
                ...bookDemoFormData,
                course: bookDemoFormData.category === 'others' ? bookDemoFormData.customCourse : bookDemoFormData.course
              };
              console.log('Book Demo Form Submitted', submissionData);
              
              const startTime = Date.now();
              
              try {
                const response = await fetch('/api/register.php', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(submissionData)
                });
                const result = await response.json();
                
                // Ensure minimum 2 seconds have passed
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                  await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                }
                
                if (result.success) {
                  setPopupType('success');
                  setPopupMessage('Submitted Successfully');
                  setShowPopup(true);
                  setShowBookDemoModal(false);
                  setBookDemoFormData({ fullName: '', mobile: '', email: '', location: '', category: '', course: '', customCourse: '' });
                } else {
                  setPopupType('error');
                  setPopupMessage('Submission Failed');
                  setShowPopup(true);
                  setShowBookDemoModal(false);
                }
              } catch (error) {
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                  await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                }
                setPopupType('error');
                setPopupMessage('Submission Failed');
                setShowPopup(true);
                setShowBookDemoModal(false);
              } finally {
                setIsSubmitting(false);
              }
            }} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input type="text" placeholder="Full Name" value={bookDemoFormData.fullName} 
                onChange={(e) => setBookDemoFormData({...bookDemoFormData, fullName: e.target.value})} 
                onKeyPress={(e) => {
                  if (!/[a-zA-Z\s]/.test(e.key)) e.preventDefault();
                }}
                aria-label="Full Name"
                required style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(15px)',
                fontSize: '0.85rem',
                color: '#1a365d',
                outline: 'none',
                boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }} />

              <input type="tel" placeholder="Mobile Number" value={bookDemoFormData.mobile} 
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setBookDemoFormData({...bookDemoFormData, mobile: value});
                }}
                pattern="[0-9]{10}"
                title="Please enter exactly 10 digits"
                aria-label="Mobile Number"
                required style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(15px)',
                fontSize: '0.85rem',
                color: '#1a365d',
                outline: 'none',
                boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }} />

              <input type="email" placeholder="Email ID" value={bookDemoFormData.email} 
                onChange={(e) => setBookDemoFormData({...bookDemoFormData, email: e.target.value})} 
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                aria-label="Email ID"
                required style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(15px)',
                fontSize: '0.85rem',
                color: '#1a365d',
                outline: 'none',
                boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }} />

              <select 
                value={bookDemoFormData.location}
                onChange={(e) => setBookDemoFormData({...bookDemoFormData, location: e.target.value})}
                required
                aria-label="Preferred Location"
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.85rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                <option value="">Preferred Location</option>
                <option value="Chrompet">Chrompet</option>
                <option value="Velachery">Velachery</option>
                <option value="Online">Online</option>
              </select>

              <select 
                value={bookDemoFormData.category}
                onChange={handleBookDemoCategoryChange}
                required
                aria-label="Course Category"
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.85rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                <option value="">Select Course Category</option>
                {Object.entries(courseCategories).map(([key, category]) => (
                  <option key={key} value={key}>{category.name}</option>
                ))}
              </select>

              {bookDemoFormData.category === 'others' ? (
                <input 
                  type="text" 
                  placeholder="Please specify your course" 
                  value={bookDemoFormData.customCourse} 
                  onChange={(e) => setBookDemoFormData({...bookDemoFormData, customCourse: e.target.value})} 
                  required
                  aria-label="Custom Course Name"
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                    backdropFilter: 'blur(15px)',
                    fontSize: '0.85rem',
                    color: '#1a365d',
                    outline: 'none',
                    boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }} 
                />
              ) : (
                <select 
                  value={bookDemoFormData.course}
                  onChange={(e) => setBookDemoFormData({...bookDemoFormData, course: e.target.value, customCourse: ''})}
                  disabled={!bookDemoFormData.category}
                  required
                  aria-label="Specific Course"
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    background: bookDemoFormData.category 
                      ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)' 
                      : 'linear-gradient(145deg, rgba(200, 200, 200, 0.7) 0%, rgba(220, 220, 220, 0.6) 50%, rgba(200, 200, 200, 0.5) 100%)',
                    backdropFilter: 'blur(15px)',
                    fontSize: '0.85rem',
                    color: bookDemoFormData.category ? '#1a365d' : '#64748b',
                    outline: 'none',
                    boxShadow: bookDemoFormData.category 
                      ? 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)' 
                      : 'inset 1px 1px 3px rgba(0, 0, 0, 0.05), inset -1px -1px 3px rgba(255, 255, 255, 0.4)',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    cursor: bookDemoFormData.category ? 'pointer' : 'not-allowed',
                    opacity: bookDemoFormData.category ? 1 : 0.7
                  }}>
                  <option value="">Select Specific Course</option>
                  {bookDemoFormData.category && courseCategories[bookDemoFormData.category].courses.map((course, index) => (
                    <option key={index} value={course}>{course}</option>
                  ))}
                </select>
              )}

              <button type="submit" disabled={isSubmitting} style={{
                background: 'linear-gradient(135deg, #00315d 0%, #004080 50%, #0066cc 100%)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.2)',
                padding: '0.65rem 1.5rem',
                borderRadius: '20px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600',
                boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)',
                marginTop: '0.3rem',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.3s ease',
                opacity: isSubmitting ? 0.7 : 1
              }}>{isSubmitting ? 'Submitting...' : 'Submit'}</button>

              <div style={{
                marginTop: '0.8rem',
                paddingTop: '0.8rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.7rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.7rem', fontWeight: '500' }}>Real-Time Project Training</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.7rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.7rem', fontWeight: '500' }}>Industry Expert Trainers</span>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.9rem', fontWeight: '700', whiteSpace: 'nowrap' }}>100% Placement Support</span>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      {/* Global Career Guidance Modal - Independent of any section */}
      {showCareerGuidanceModal && ReactDOM.createPortal((
        <div onClick={() => setShowCareerGuidanceModal(false)} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999999999,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <div onClick={(e) => e.stopPropagation()} className="career-guidance-modal" style={{
            background: 'linear-gradient(135deg, #002a5c 0%, #004080 30%, #0066cc 70%, #3b82f6 100%)',
            backdropFilter: 'blur(25px)',
            WebkitBackdropFilter: 'blur(25px)',
            padding: '1rem',
            paddingTop: '3rem',
            borderRadius: '25px',
            width: '95%',
            maxWidth: '400px',
            maxHeight: '85vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.4), inset 0 -2px 4px rgba(0, 0, 0, 0.1), 0 15px 35px rgba(59, 130, 246, 0.25)',
            border: '2px solid rgba(255, 255, 255, 0.3)'
          }}>
            <div onClick={() => setShowCareerGuidanceModal(false)} style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              fontSize: '2rem',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'scale(1)';
            }}>×</div>

            <h3 style={{
              color: '#ffffff',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '0.3rem',
              marginTop: '1rem',
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>Get Free Career Guidance</h3>
            
            <p style={{
              color: '#e0e7ff',
              fontSize: '0.85rem',
              textAlign: 'center',
              marginBottom: '1rem',
              lineHeight: '1.3',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
            }}>Connect with our expert counselor for personalized guidance</p>

            <form onSubmit={async (e) => { 
              e.preventDefault();
              if (isSubmitting) return;
              
              // Validate mobile number
              if (guidanceFormData.mobile.length !== 10) {
                alert('Please enter a valid 10-digit mobile number');
                return;
              }
              
              setIsSubmitting(true);
              const submissionData = {
                ...guidanceFormData,
                course: guidanceFormData.category === 'others' ? guidanceFormData.customCourse : guidanceFormData.course
              };
              console.log('Career Guidance Form Submitted', submissionData);
              
              const startTime = Date.now();
              
              try {
                const response = await fetch('/api/register.php', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(submissionData)
                });
                const result = await response.json();
                
                // Ensure minimum 2 seconds have passed
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                  await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                }
                
                if (result.success) {
                  setPopupType('success');
                  setPopupMessage('Submitted Successfully');
                  setShowPopup(true);
                  setShowCareerGuidanceModal(false);
                  setGuidanceFormData({ fullName: '', mobile: '', email: '', location: '', category: '', course: '', customCourse: '' });
                } else {
                  setPopupType('error');
                  setPopupMessage('Submission Failed');
                  setShowPopup(true);
                  setShowCareerGuidanceModal(false);
                }
              } catch (error) {
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                  await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                }
                setPopupType('error');
                setPopupMessage('Submission Failed');
                setShowPopup(true);
                setShowCareerGuidanceModal(false);
              } finally {
                setIsSubmitting(false);
              }
            }} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input type="text" placeholder="Full Name" value={guidanceFormData.fullName} 
                onChange={(e) => setGuidanceFormData({...guidanceFormData, fullName: e.target.value})} 
                onKeyPress={(e) => {
                  if (!/[a-zA-Z\s]/.test(e.key)) e.preventDefault();
                }}
                aria-label="Full Name"
                required style={{
                padding: '0.4rem 0.6rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(15px)',
                fontSize: '0.9rem',
                color: '#1a365d',
                outline: 'none',
                boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }} />

              <input type="tel" placeholder="Mobile Number" value={guidanceFormData.mobile} 
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setGuidanceFormData({...guidanceFormData, mobile: value});
                }}
                pattern="[0-9]{10}"
                title="Please enter exactly 10 digits"
                aria-label="Mobile Number"
                required style={{
                padding: '0.4rem 0.6rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(15px)',
                fontSize: '0.9rem',
                color: '#1a365d',
                outline: 'none',
                boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }} />

              <input type="email" placeholder="Email ID" value={guidanceFormData.email} 
                onChange={(e) => setGuidanceFormData({...guidanceFormData, email: e.target.value})} 
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                aria-label="Email ID"
                required style={{
                padding: '0.4rem 0.6rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(15px)',
                fontSize: '0.9rem',
                color: '#1a365d',
                outline: 'none',
                boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }} />

              <select value={guidanceFormData.location} onChange={(e) => setGuidanceFormData({...guidanceFormData, location: e.target.value})} required aria-label="Preferred Location" style={{
                padding: '0.4rem 0.6rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(15px)',
                fontSize: '0.9rem',
                color: '#1a365d',
                outline: 'none',
                boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}>
                <option value="">Preferred Location</option>
                <option value="Chrompet">Chrompet</option>
                <option value="Velachery">Velachery</option>
                <option value="Online">Online</option>
              </select>

              <select value={guidanceFormData.category} onChange={handleGuidanceCategoryChange} required aria-label="Course Category" style={{
                padding: '0.4rem 0.6rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(15px)',
                fontSize: '0.9rem',
                color: '#1a365d',
                outline: 'none',
                boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}>
                <option value="">Select Course Category</option>
                {Object.entries(courseCategories).map(([key, category]) => (
                  <option key={key} value={key}>{category.name}</option>
                ))}
              </select>

              {guidanceFormData.category === 'others' ? (
                <input 
                  type="text" 
                  placeholder="Please specify your course" 
                  value={guidanceFormData.customCourse} 
                  onChange={(e) => setGuidanceFormData({...guidanceFormData, customCourse: e.target.value})} 
                  required
                  aria-label="Custom Course Name"
                  style={{
                    padding: '0.4rem 0.6rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                    backdropFilter: 'blur(15px)',
                    fontSize: '0.9rem',
                    color: '#1a365d',
                    outline: 'none',
                    boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }} 
                />
              ) : (
                <select value={guidanceFormData.course} onChange={(e) => setGuidanceFormData({...guidanceFormData, course: e.target.value, customCourse: ''})} disabled={!guidanceFormData.category} required aria-label="Specific Course" style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: guidanceFormData.category 
                    ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)' 
                    : 'linear-gradient(145deg, rgba(200, 200, 200, 0.7) 0%, rgba(220, 220, 220, 0.6) 50%, rgba(200, 200, 200, 0.5) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.9rem',
                  color: guidanceFormData.category ? '#1a365d' : '#64748b',
                  outline: 'none',
                  boxShadow: guidanceFormData.category 
                    ? 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)' 
                    : 'inset 1px 1px 3px rgba(0, 0, 0, 0.05), inset -1px -1px 3px rgba(255, 255, 255, 0.4)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  cursor: guidanceFormData.category ? 'pointer' : 'not-allowed',
                  opacity: guidanceFormData.category ? 1 : 0.7
                }}>
                  <option value="">Select Specific Course</option>
                  {guidanceFormData.category && courseCategories[guidanceFormData.category].courses.map((course, index) => (
                    <option key={index} value={course}>{course}</option>
                  ))}
                </select>
              )}

              <button type="submit" disabled={isSubmitting} style={{
                background: 'linear-gradient(135deg, #00315d 0%, #004080 50%, #0066cc 100%)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.2)',
                padding: '0.7rem 1.5rem',
                borderRadius: '20px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)',
                marginTop: '0.3rem',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.3s ease',
                opacity: isSubmitting ? 0.7 : 1
              }}>{isSubmitting ? 'Submitting...' : 'Submit'}</button>

              <div style={{
                marginTop: '0.8rem',
                paddingTop: '0.8rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', flexWrap: 'wrap', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.7rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.7rem', fontWeight: '500' }}>Real-Time Project Training</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.7rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.7rem', fontWeight: '500' }}>Industry Expert Trainers</span>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.9rem', fontWeight: '700', whiteSpace: 'nowrap' }}>100% Placement Support</span>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      {/* Enrollment Modal */}
      {showEnrollmentModal && ReactDOM.createPortal((
        <div onClick={() => setShowEnrollmentModal(false)} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999999999,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <div onClick={(e) => e.stopPropagation()} className="enrollment-modal" style={{
            background: 'linear-gradient(135deg, #002a4d 0%, #004b7a 50%, #0066a6 100%)',
            backdropFilter: 'blur(25px)',
            borderRadius: '25px',
            padding: '1rem',
            paddingTop: '2rem',
            width: '95%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.4), inset 0 -2px 4px rgba(0, 0, 0, 0.1), 0 15px 35px rgba(59, 130, 246, 0.25)',
            border: '2px solid rgba(255, 255, 255, 0.3)'
          }}>
            <div onClick={() => setShowEnrollmentModal(false)} style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              fontSize: '2rem',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'scale(1)';
            }}>×</div>

            <h3 style={{
              color: '#ffffff',
              fontSize: 'clamp(1.1rem, 3vw, 1.5rem)',
              fontWeight: '700',
              marginBottom: '0.2rem',
              marginTop: '1rem',
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>Start your learning journey</h3>

            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.75rem',
              textAlign: 'center',
              marginBottom: '0.8rem',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
            }}>Fill your details to enroll for the course</p>

            <form onSubmit={async (e) => { 
              e.preventDefault();
              if (isSubmitting) return;
              
              // Validate mobile number
              if (enrollmentFormData.mobile.length !== 10) {
                alert('Please enter a valid 10-digit mobile number');
                return;
              }
              
              setIsSubmitting(true);
              const submissionData = {
                ...enrollmentFormData,
                course: enrollmentFormData.category === 'others' ? enrollmentFormData.customCourse : enrollmentFormData.course
              };
              console.log('Enrollment Form Submitted', submissionData);
              
              const startTime = Date.now();
              
              try {
                const response = await fetch('/api/register.php', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(submissionData)
                });
                const result = await response.json();
                
                // Ensure minimum 2 seconds have passed
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                  await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                }
                
                if (result.success) {
                  setPopupType('success');
                  setPopupMessage('Submitted Successfully');
                  setShowPopup(true);
                  setShowEnrollmentModal(false);
                  setEnrollmentFormData({ fullName: '', mobile: '', email: '', location: '', category: '', course: '', customCourse: '' });
                } else {
                  setPopupType('error');
                  setPopupMessage('Submission Failed');
                  setShowPopup(true);
                  setShowEnrollmentModal(false);
                }
              } catch (error) {
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                  await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                }
                setPopupType('error');
                setPopupMessage('Submission Failed');
                setShowPopup(true);
                setShowEnrollmentModal(false);
              } finally {
                setIsSubmitting(false);
              }
            }} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input 
                type="text" 
                placeholder="Full Name" 
                required 
                value={enrollmentFormData.fullName}
                onChange={(e) => setEnrollmentFormData({...enrollmentFormData, fullName: e.target.value})}
                onKeyPress={(e) => {
                  if (!/[a-zA-Z\s]/.test(e.key)) e.preventDefault();
                }}
                aria-label="Full Name"
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.85rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
              }} />

              <input 
                type="tel" 
                placeholder="Mobile Number" 
                required 
                value={enrollmentFormData.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setEnrollmentFormData({...enrollmentFormData, mobile: value});
                }}
                pattern="[0-9]{10}"
                title="Please enter exactly 10 digits"
                aria-label="Mobile Number"
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.85rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
              }} />

              <input 
                type="email" 
                placeholder="Email ID" 
                required 
                value={enrollmentFormData.email}
                onChange={(e) => setEnrollmentFormData({...enrollmentFormData, email: e.target.value})}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                aria-label="Email ID"
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.85rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
              }} />

              <select 
                value={enrollmentFormData.location}
                onChange={(e) => setEnrollmentFormData({...enrollmentFormData, location: e.target.value})}
                required
                className="custom-select"
                aria-label="Preferred Location"
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.85rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                <option value="">Preferred Location</option>
                <option value="Chrompet">Chrompet</option>
                <option value="Velachery">Velachery</option>
                <option value="Online">Online</option>
              </select>

              <select 
                value={enrollmentFormData.category}
                onChange={handleEnrollmentCategoryChange}
                required
                className="custom-select"
                aria-label="Course Category"
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.85rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                <option value="">Select Course Category</option>
                {Object.entries(courseCategories).map(([key, category]) => (
                  <option key={key} value={key}>{category.name}</option>
                ))}
              </select>

              {enrollmentFormData.category === 'others' ? (
                <input 
                  type="text" 
                  placeholder="Please specify your course" 
                  value={enrollmentFormData.customCourse} 
                  onChange={(e) => setEnrollmentFormData({...enrollmentFormData, customCourse: e.target.value})} 
                  required
                  aria-label="Custom Course Name"
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                    backdropFilter: 'blur(15px)',
                    fontSize: '0.85rem',
                    color: '#1a365d',
                    outline: 'none',
                    boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }} 
                />
              ) : (
                <select 
                  value={enrollmentFormData.course}
                  onChange={(e) => setEnrollmentFormData({...enrollmentFormData, course: e.target.value, customCourse: ''})}
                  disabled={!enrollmentFormData.category}
                  required
                  className="custom-select"
                  aria-label="Specific Course"
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    background: enrollmentFormData.category 
                      ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)' 
                      : 'linear-gradient(145deg, rgba(200, 200, 200, 0.7) 0%, rgba(220, 220, 220, 0.6) 50%, rgba(200, 200, 200, 0.5) 100%)',
                    backdropFilter: 'blur(15px)',
                    fontSize: '0.85rem',
                    color: enrollmentFormData.category ? '#1a365d' : '#64748b',
                    outline: 'none',
                    boxShadow: enrollmentFormData.category 
                      ? 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)' 
                      : 'inset 1px 1px 3px rgba(0, 0, 0, 0.05), inset -1px -1px 3px rgba(255, 255, 255, 0.4)',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    cursor: enrollmentFormData.category ? 'pointer' : 'not-allowed',
                    opacity: enrollmentFormData.category ? 1 : 0.7
                  }}>
                  <option value="">Select Specific Course</option>
                  {enrollmentFormData.category && courseCategories[enrollmentFormData.category].courses.map((course, index) => (
                    <option key={index} value={course}>{course}</option>
                  ))}
                </select>
              )}

              <button type="submit" disabled={isSubmitting} style={{
                background: 'linear-gradient(135deg, #00315d 0%, #004080 50%, #0066cc 100%)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.2)',
                padding: '0.65rem 1.5rem',
                borderRadius: '20px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600',
                boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)',
                marginTop: '0.3rem',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.3s ease',
                opacity: isSubmitting ? 0.7 : 1
              }}>{isSubmitting ? 'Submitting...' : 'Confirm Enrollment'}</button>

              <div style={{
                marginTop: '0.8rem',
                paddingTop: '0.8rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.7rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.7rem', fontWeight: '500' }}>Real-Time Project Training</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.7rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.7rem', fontWeight: '500' }}>Industry Expert Trainers</span>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: '700' }}>✔</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.9rem', fontWeight: '700', whiteSpace: 'nowrap' }}>100% Placement Support</span>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      <PixelTracker />
      <FormSuccessPopup 
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        type={popupType}
        message={popupMessage}
      />
      <FormLoader isLoading={isSubmitting} />
      <BackToTop />
    </div>
  );
}

// Wrapper component to handle URL params
function CourseDetailsWrapper(props) {
  const { courseSlug } = useParams();
  
  // Convert slug back to course name
  const courseName = slugToCourse(courseSlug);
  
  return (
    <CourseDetails 
      course={courseName}
      activePage="course-details"
      {...props}
    />
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;